import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { detectBrowser } from "../../utils/browserUtils";
import { runFirefoxCompatibilityTests } from "../../utils/firefoxTest";
import LoadingOverlay from "../../components/UI/LoadingOverlay/LoadingOverlay";
import styles from "./AuthPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";

const OAUTH_ERROR_MESSAGES = {
  auth_failed: "Google sign-in did not complete. Please try again.",
  login_error: "Could not finish sign-in. Please try again.",
  session_error: "Session could not be saved. Please try signing in again.",
  callback_error: "Sign-in callback failed. Please try again.",
};

export default function AuthPage() {
  const [error, setError] = useState("");
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showFirefoxTest, setShowFirefoxTest] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const {
    isAuthenticated,
    login,
    loginWithEmailPassword,
    loading: authLoading,
    browserCompatibility,
    refreshAuthStatus,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      setIsInitialLoad(false);
    }
  }, [authLoading]);

  useEffect(() => {
    const hasGoogleParams =
      searchParams.get("code") || searchParams.get("state");

    if (
      hasGoogleParams &&
      !authLoading &&
      typeof refreshAuthStatus === "function"
    ) {
      refreshAuthStatus();
    }
  }, [searchParams, authLoading, refreshAuthStatus]);

  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }

    const authError = searchParams.get("error");
    if (
      authError === "auth_failed" ||
      authError === "login_error" ||
      authError === "session_error" ||
      authError === "callback_error"
    ) {
      setError(
        OAUTH_ERROR_MESSAGES[authError] || "Sign-in failed. Please try again."
      );
      navigate("/auth", { replace: true });
      return;
    }
    if (authError === "email_mismatch") {
      setError(
        "The Google account does not match the email required for this step."
      );
      navigate("/auth", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, searchParams]);

  const handleEmailNext = () => {
    if (email.trim()) {
      setStep("password");
      setError("");
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
  };

  const handlePasswordLogin = async () => {
    if (!password.trim()) return;

    setError("");

    try {
      await loginWithEmailPassword(email, password);
    } catch (err) {
      setError(err.message || "Failed to login. Please try again.");
    }
  };

  const handleGoogleLogin = async () => {
    setError("");

    try {
      await login();
    } catch {
      setError("Failed to initiate login. Please try again.");
    }
  };

  const handleFirefoxTest = () => {
    const results = runFirefoxCompatibilityTests();
    setTestResults(results);
    setShowFirefoxTest(true);
  };

  const isFirefox = detectBrowser() === "firefox";

  if (isInitialLoad) {
    return (
      <LoadingOverlay
        isVisible={true}
        message="Loading..."
        minDisplayTime={1500}
      />
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Welcome Back</h1>
          <p className={styles.subtitle}>
            Sign in to your account to access your dashboard and manage your
            projects
          </p>

          {browserCompatibility && !browserCompatibility.compatible && (
            <div className={styles.browserWarning}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.warningIcon}
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div>
                <strong>Browser Compatibility Warning</strong>
                <p>
                  Your browser ({browserCompatibility.browser}) may have issues
                  with authentication.
                  {browserCompatibility.browser === "safari" &&
                    " Please ensure third-party cookies are enabled."}
                  {browserCompatibility.browser === "firefox" && (
                    <span>
                      {" "}
                      Firefox has strict privacy settings that may affect
                      authentication. Our system now uses multiple
                      authentication methods to ensure compatibility. If you
                      experience issues, try refreshing the page or clearing
                      your browser cache.
                    </span>
                  )}
                </p>
                {browserCompatibility.issues &&
                  browserCompatibility.issues.length > 0 && (
                    <ul className={styles.browserIssuesList}>
                      {browserCompatibility.issues.map((issue, index) => (
                        <li key={index}>{issue}</li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className={styles.loginForm}>
            <div className={styles.inputGroup}>
              <div className={styles.inputContainer}>
                <div
                  className={`${styles.inputWrapper} ${
                    step === "email" ? styles.slideIn : styles.slideOut
                  }`}
                >
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.input}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailNext()}
                  />
                </div>

                <div
                  className={`${styles.inputWrapper} ${
                    step === "password" ? styles.slideIn : styles.slideOut
                  }`}
                >
                  <div className={styles.passwordHeader}>
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className={styles.backButton}
                    >
                      ← Back
                    </button>
                    <span className={styles.emailDisplay}>{email}</span>
                  </div>
                  <div className={styles.passwordInputContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={styles.passwordInput}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handlePasswordLogin()
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={styles.passwordToggle}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={
                  step === "email" ? handleEmailNext : handlePasswordLogin
                }
                disabled={step === "email" ? !email.trim() : !password.trim()}
                className={styles.actionButton}
              >
                {step === "email" ? "Next" : "Sign In"}
              </button>
            </div>
          </div>

          <div className={styles.divider}>
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            className={styles.googleButton}
          >
            <div className={styles.buttonContent}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                className={styles.googleIcon}
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className={styles.buttonText}>Continue with Google</span>
            </div>
          </button>

          <div className={styles.googleHelp}>
            <p>Already have an account? Sign in with Google above.</p>
            <p>
              <a href="/forgot-password" className={styles.forgotPasswordLink}>
                Forgot your password?
              </a>
            </p>
            {isFirefox && (
              <p>
                <button
                  onClick={handleFirefoxTest}
                  className={styles.firefoxTestButton}
                  type="button"
                >
                  Test Firefox Compatibility
                </button>
              </p>
            )}
            <p>
              Having issues signing in?{" "}
              <a
                href="https://softindex.ro/contact"
                className={styles.registerLink}
              >
                Contact our support team
              </a>
            </p>
          </div>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              By signing in, you agree to our{" "}
              <a href="/terms" className={styles.footerLink}>
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className={styles.footerLink}>
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>

      {showFirefoxTest && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Firefox Compatibility Test Results</h3>
              <button
                type="button"
                onClick={() => setShowFirefoxTest(false)}
                className={styles.closeButton}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {testResults && (
                <div>
                  <p>
                    <strong>Browser:</strong> {testResults.browser}
                  </p>
                  <p>
                    <strong>Compatible:</strong>{" "}
                    {testResults.compatible ? "Yes" : "No"}
                  </p>
                  {testResults.issues && testResults.issues.length > 0 && (
                    <div>
                      <p>
                        <strong>Issues Found:</strong>
                      </p>
                      <ul>
                        {testResults.issues.map((issue, index) => (
                          <li key={index}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className={styles.troubleshootingSteps}>
                    <h4>Troubleshooting Steps:</h4>
                    <ol>
                      <li>
                        Our system now uses JWT tokens as a fallback for Firefox
                        users
                      </li>
                      <li>Try refreshing the page if authentication fails</li>
                      <li>
                        Clear your browser cache and cookies for this site
                      </li>
                      <li>Ensure JavaScript is enabled</li>
                      <li>
                        If issues persist, try using a different browser
                        temporarily
                      </li>
                    </ol>
                    <p className={styles.modalNote}>
                      <strong>Note:</strong> You no longer need to disable
                      Enhanced Tracking Protection. Our authentication system
                      automatically adapts to Firefox&apos;s privacy settings.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                onClick={() => setShowFirefoxTest(false)}
                className={styles.modalButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
